import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  PhoneCall, CalendarClock, AlertTriangle, Building2, Plus, MessageSquarePlus,
  TrendingUp, Euro, ChevronRight, ClipboardList, Video, Briefcase, Rocket,
} from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { LogConversationModal } from '../components/crm/LogConversationModal';
import { TaskModal } from '../components/crm/TaskModal';
import KpiCard from '../components/dashboard/KpiCard';
import {
  HEALTH_META, priorityMeta, fmtDate, daysAgoLabel, contactName, todayISO, fmtMoney,
} from '../components/crm/constants';

const PipelineStat = ({ label, value, tone }) => (
  <div className="text-center">
    <p className={`text-lg font-bold ${tone || 'text-gray-900'}`}>{value}</p>
    <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
  </div>
);

export default function SalesDashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tableMissing, setTableMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskFilter, setTaskFilter] = useState('today');

  const load = useCallback(async () => {
    const res = await crmFetch('/dashboard');
    if (res.tableMissing) setTableMissing(true);
    else if (res.success) { setData(res.data); setTableMissing(false); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = userProfile?.firstName || '';

  const completeTask = async (task) => {
    const res = await crmFetch(`/tasks/${task.id}`, { method: 'PATCH', body: JSON.stringify({ status: 'completed' }) });
    if (res.success) { toast.success('Task completed'); load(); }
    else toast.error(res.message || 'Failed to update task');
  };

  const today = todayISO();
  const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const allTasks = data?.tasks || [];
  const filteredTasks = allTasks.filter((t) => {
    if (taskFilter === 'today') return t.due_date === today;
    if (taskFilter === 'overdue') return t.overdue;
    if (taskFilter === 'week') return t.due_date && t.due_date >= today && t.due_date <= weekEnd;
    return true;
  });

  const filters = [
    { key: 'today', label: 'Today' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'week', label: 'This Week' },
    { key: 'all', label: 'All' },
  ];

  return (
    <SalesLayout>
      <div data-testid="sales-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight text-slate-900">{greeting}{firstName ? `, ${firstName}` : ''}</h1>
            <p className="text-sm text-slate-500 mt-1">What I need to do today.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTaskOpen(true)} data-testid="dashboard-new-task-btn">
              <Plus className="w-4 h-4 mr-2" /> New Task
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setLogOpen(true)} data-testid="dashboard-log-conversation-btn">
              <MessageSquarePlus className="w-4 h-4 mr-2" /> Log Conversation
            </Button>
          </div>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50 mb-8" data-testid="crm-migration-notice">
            <CardContent className="p-6">
              <h2 className="font-semibold text-amber-900 mb-1">CRM setup pending</h2>
              <p className="text-sm text-amber-800">
                The CRM database tables haven&apos;t been created yet. Run the migration
                <code className="mx-1 px-1 bg-amber-100 rounded">2026-08-30-crm-phase1.sql</code>
                in the Supabase SQL Editor, then refresh this page.
              </p>
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KpiCard icon={CalendarClock} label="Tasks Today" value={loading ? '…' : data?.kpis?.tasksToday ?? 0} tone="blue" testId="kpi-tasks-today" />
              <KpiCard icon={PhoneCall} label="Calls Today" value={loading ? '…' : data?.kpis?.callsToday ?? 0} tone="emerald" testId="kpi-calls-today" />
              <KpiCard icon={Briefcase} label="Open Jobs" value={loading ? '…' : data?.kpis?.activeJobs ?? 0} tone="violet" testId="kpi-open-jobs" />
              <KpiCard icon={AlertTriangle} label="Overdue" value={loading ? '…' : data?.kpis?.overdueTasks ?? 0} tone="rose" testId="kpi-overdue-tasks" />
            </div>

            {/* My Pipeline + My Commission + Software Pipeline */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/crm-candidates')} data-testid="pipeline-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                    <CardTitle className="text-sm text-gray-700">My Pipeline</CardTitle>
                    <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-2">
                    <PipelineStat label="Active Jobs" value={data?.pipeline?.activeJobs ?? 0} />
                    <PipelineStat label="In Process" value={data?.pipeline?.inProcess ?? 0} />
                    <PipelineStat label="Presented" value={data?.pipeline?.presented ?? 0} />
                    <PipelineStat label="Interviews" value={data?.pipeline?.interviews ?? 0} />
                    <PipelineStat label="Placements" value={data?.pipeline?.placements ?? 0} tone="text-emerald-600" />
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/crm-commission')} data-testid="commission-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Euro className="w-4 h-4 text-emerald-500" />
                    <CardTitle className="text-sm text-gray-700">My Commission</CardTitle>
                    <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <p className="text-xs text-gray-400 py-3 text-center">Loading…</p>
                  ) : data?.commission ? (
                    <div className="grid grid-cols-3 gap-2">
                      <PipelineStat label="Earned (YTD)" value={fmtMoney(data.commission.earnedYtd ?? 0) || '€0'} tone="text-emerald-600" />
                      <PipelineStat label="Pending" value={fmtMoney(data.commission.pending ?? 0) || '€0'} tone="text-amber-600" />
                      <PipelineStat label="Pipeline Potential" value={fmtMoney(data.commission.potentialCommission ?? 0) || '€0'} />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 py-2 text-center">
                      Run <code className="px-1 bg-gray-100 rounded">2026-06-16-crm-phase3.sql</code> in Supabase to unlock commission tracking.
                    </p>
                  )}
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/software-pipeline')} data-testid="software-pipeline-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Rocket className="w-4 h-4 text-violet-500" />
                    <CardTitle className="text-sm text-gray-700">Software Pipeline</CardTitle>
                    <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <p className="text-xs text-gray-400 py-3 text-center">Loading…</p>
                  ) : data?.softwarePipeline ? (
                    <div className="grid grid-cols-3 gap-2">
                      <PipelineStat label="Open ARR" value={fmtMoney(data.softwarePipeline.openArr ?? 0) || '€0'} />
                      <PipelineStat label="Won ARR" value={fmtMoney(data.softwarePipeline.wonArrYtd ?? 0) || '€0'} tone="text-emerald-600" />
                      <PipelineStat label="Win Rate" value={`${data.softwarePipeline.winRate ?? 0}%`} tone="text-violet-600" />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 py-2 text-center">
                      Run <code className="px-1 bg-gray-100 rounded">2026-06-17-crm-phase6.sql</code> in Supabase to unlock software deals.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Priorities Today — full width */}
            <Card className="mb-6" data-testid="priorities-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Priorities Today
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(data?.priorities || []).length === 0 && (
                  <p className="text-sm text-gray-500 py-4 text-center">Nothing urgent — you&apos;re all caught up 🎉</p>
                )}
                {(data?.priorities || []).map((p, i) => (
                  <button
                    key={i}
                    onClick={() => p.accountId && navigate(`/accounts/${p.accountId}`)}
                    className={`w-full text-left flex items-center justify-between rounded-lg border-l-4 px-4 py-2.5 transition-colors ${
                      p.kind === 'overdue' ? 'border-rose-500 bg-rose-50 hover:bg-rose-100'
                        : p.kind === 'due_today' ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
                          : 'border-amber-500 bg-amber-50 hover:bg-amber-100'
                    }`}
                    data-testid={`priority-item-${i}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{p.label}</p>
                      <p className="text-xs text-gray-500">{p.sub}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                  </button>
                ))}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* My Tasks */}
              <Card className="lg:col-span-2" data-testid="my-tasks-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-blue-500" /> My Tasks
                    </CardTitle>
                    <div className="flex gap-1">
                      {filters.map((f) => (
                        <button
                          key={f.key}
                          onClick={() => setTaskFilter(f.key)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                            taskFilter === f.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          data-testid={`task-filter-${f.key}`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-1">
                  {filteredTasks.length === 0 && (
                    <p className="text-sm text-gray-500 py-4 text-center">No tasks in this view.</p>
                  )}
                  {filteredTasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50" data-testid={`dashboard-task-${t.id}`}>
                      <Checkbox onCheckedChange={() => completeTask(t)} data-testid={`task-complete-${t.id}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{t.title}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {t.account?.name || '—'}{t.contact ? ` · ${contactName(t.contact)}` : ''}
                        </p>
                      </div>
                      <Badge className={`${priorityMeta(t.priority).className} border-0`}>{priorityMeta(t.priority).label}</Badge>
                      <span className={`text-xs whitespace-nowrap ${t.overdue ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                        {t.due_date ? fmtDate(t.due_date) : 'No date'}{t.overdue ? ' · Overdue' : ''}
                      </span>
                    </div>
                  ))}
                  <div className="pt-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/sales-tasks')} data-testid="view-all-tasks-btn">
                      View all tasks <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* My Accounts */}
              <Card data-testid="my-accounts-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-500" /> My Accounts
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/accounts')} data-testid="view-all-accounts-btn">All</Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-1">
                  {(data?.accounts || []).length === 0 && (
                    <p className="text-sm text-gray-500 py-4 text-center">No accounts yet — create your first one.</p>
                  )}
                  {(data?.accounts || []).slice(0, 6).map((a) => (
                    <button
                      key={a.id}
                      onClick={() => navigate(`/accounts/${a.id}`)}
                      className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 text-left"
                      data-testid={`dashboard-account-${a.id}`}
                    >
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${HEALTH_META[a.health].dot}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{a.name}</p>
                        <p className="text-xs text-gray-500">Last contact: {daysAgoLabel(a.daysSinceContact)}</p>
                      </div>
                      {a.overdueTaskCount > 0 && (
                        <Badge className="bg-rose-100 text-rose-700 border-0">{a.overdueTaskCount} overdue</Badge>
                      )}
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>

      <LogConversationModal open={logOpen} onOpenChange={setLogOpen} onSaved={load} />
      <TaskModal open={taskOpen} onOpenChange={setTaskOpen} onSaved={load} />
    </SalesLayout>
  );
}
